import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';

import request from 'superagent';

import parseJSON from 'shared-utils/src/parseJSON';
import addToRecentlySearchedLocalities from 'shared-utils/src/addToRecentlySearchedLocalities';
import getActiveFilters from 'shared-utils/src/getActiveFilters';
import RESUME_LS_RECORD from 'shared-constants/src/resumeLSRecord';

import PAGES_TYPE from 'shared-constants/src/pagesType';
import { DEFAULT_FILTERS_CONFIGURATION } from 'shared-constants/src/filters';

import SearchBuilder from 'shared-components/components/SearchBuilder';
import defaultFiltersForSearchBuilder from 'shared-constants/src/defaultFiltersForSearchBuilder';

import { getItem } from '@helpers/Storage';
import throwApireqDebugError from '@helpers/throwApireqDebugError';

import { fetchHome } from '../../common/redux/home/actions';

import UiHandlerHOC from '../app/UiHandler';
import { fetchLocationById } from '../components/common/search/helpers/search';
import { normalizeSRPUrl } from '../helpers/search/Search';
import { parseResumeRecord } from './parseResumeRecord';

import Home from './Home';

import { filtersAndLocationsToQObject } from '../../common/lib/filtersToQObject';

const checkUsableFiltersValidity = (filters) => {
  const usableCategoryValues = DEFAULT_FILTERS_CONFIGURATION.propertyTypeGroup.values.map((v) => v.value);
  const usableChannelValues = DEFAULT_FILTERS_CONFIGURATION.tr.values.map((v) => v.value);

  console.log({ filters });

  if (!usableCategoryValues.includes(filters.propertyTypeGroup)) {
    filters.propertyTypeGroup = defaultFiltersForSearchBuilder.propertyTypeGroup;
  }
  if (!usableChannelValues.includes(filters.tr)) {
    filters.tr = defaultFiltersForSearchBuilder.tr;
  }

  /**
   * TECH-7028 - hasAC is a boolean, but it's passed as an array in old configuration
   * so we need to convert it to a boolean and set it to false if it's an array with only 'all' value
   * TODO: remove this when localStorage will be removed with this configuration
   */
  if (Array.isArray(filters.hasAC)) {
    if (filters.hasAC.length === 1 && filters.hasAC[0] === 'all') {
      filters.hasAC = false;
    } else {
      filters.hasAC = true;
    }
  }

  const { price, mqPrice, payment } = filters;

  if (!price.min && !price.max) {
    if (mqPrice.min || mqPrice.max) {
      filters.priceType = 'mqPrice';
    } else if (payment.min || payment.max) {
      filters.priceType = 'payment';
    }
  }

  const v13_to_v10 = { 0: 'piano terra', 999: 'intermedio', 1000: 'ultimo' };
  if (filters.level && v13_to_v10[filters.level]) {
    filters.level = v13_to_v10[filters.level];
  }
};

const HomePageComponent = (props) => {
  const {
    config: {
      esapi,
      siteName,
      layout: {
        recentSearchesLocalStorageKey,
      },
    },
    home: {
      filters,
      isFetchingResults,
      availableResults,
      availablePoi,
    },
    intl: {
      currentLocale,
    },
    actions: {
      fetchAvailableResults,
    },
    location: {
      pathname,
    },
    isLocalStorageAvailable,
    navigationCycle,
  } = props;
  
  const [initialFilters, setInitialFilters] = useState({ ...defaultFiltersForSearchBuilder, tr: filters.tr });
  const [activeFiltersOnSSr, setActiveFiltersOnSSr] = useState({});
  const [rawQObject, setRawQObject] = useState(null);
  const [initialLocations, setInitialLocations] = useState({
    locations: null,
    parent: null,
  });
  const [recentSearches, setRecentSearches] = useState(null);
  const [available, setAvailable] = useState('');
  const [availablePois, setAvailablePois] = useState(availablePoi);
  const [hasLSSearch, setHasLSSearch] = useState(false);
  const [filtersDialog, setFiltersDialog] = useState(null);
  const [resetDialog, setResetDialog] = useState(null);
  const [eraseAddressesDialog, setEraseAddressesDialog] = useState(null);
  
  /** used by FiltersBridge */
  const [tempFilters, setTempFilters] = useState(defaultFiltersForSearchBuilder);
  const [hasEditedtempFilters, setHasEditedTempFilters] = useState(false);
  const updateTempFilters = (newFilter, clb = null) => {
    setTempFilters((state) => ({ ...state, ...newFilter }));
    if (!hasEditedtempFilters) {
      setHasEditedTempFilters(true);
    }
    clb && setTimeout(() => clb(), 10);
  };
  /** used by FiltersBridge */

  const computeAPIReq = async ({
    locations,
    filters,
    forceMap = false,
  } = {}) => {
    let qObject;
    try {
      qObject = filtersAndLocationsToQObject(filters, locations);
      const proxy = await request.post('/portal-srp/api/v1/apireq')
        .set('Content-Type', 'application/json')
        .set('Accept', 'application/json')
        .withCredentials()
        .send({
          qObject,
          locations,
          apireqParams: {
            pathname,
            domain: siteName,
            locale: currentLocale,
            esapi,
            publisherPage: false,
          },
          pageType: forceMap ? PAGES_TYPE.SRP_MAP : PAGES_TYPE.HOME_PAGE,
        });
      const { body: { apireq, uri, queryFilters } } = proxy;
      return { apireq, uri, queryFilters, locale: currentLocale };
    } catch (error) {
      throwApireqDebugError('HomePage', error, locations, qObject);
    }
  };

  const fetchResults = async ({
    locations,
    filters,
  }) => {
    if (!locations.length && !filters.nearby) {
      setAvailable('');
    } else {
      const apireq = await computeAPIReq({
        locations,
        filters,
      });
      if (!apireq) return;
      fetchAvailableResults(apireq);
    }
  };

  const fetchSBAvailableResults = async ({
    locations,
    filters,
  }) => {
    const apireqResult = await computeAPIReq({
      locations,
      filters,
    });
    if (!apireqResult) return { total: 0, ptgrpsAggregates: null, nearbyGeoJSON: null };

    const proxy = await request.post('/portal-srp/api/v1/search/availableresults')
      .set('Content-Type', 'application/json')
      .set('Accept', 'application/json')
      .withCredentials()
      .send({ apireq: apireqResult.apireq, queryFilters: apireqResult.queryFilters });

    setAvailablePois(proxy?.body?.availablePoi || []);

    return {
      total: proxy?.body?.total || 0,
      ptgrpsAggregates: proxy?.body?.ptgrpsAggregates || null,
      nearbyGeoJSON: proxy?.body?.nearbyGeoJSON || null,
    };
  };

  const submitSearch = async ({
    locations,
    filters: submitSearchFilters,
    forceMap,
  }) => {
    const apireq = await computeAPIReq({
      locations,
      filters: submitSearchFilters,
      forceMap,
    });
    if (!apireq) return;
    if (isLocalStorageAvailable) {
      addToRecentlySearchedLocalities(locations);
      // spostiamo il salvataggio del record RLS all'atterraggio in SRP o Mappa
      // const rlsObj = { filters: submitSearchFilters, locations, parentLocation };
      // setItem(RESUME_LS_RECORD, JSON.stringify(rlsObj));
    }
    document.location = normalizeSRPUrl(apireq.uri);
  };

  useEffect(
    () => {
      if (window.localStorage) {
        try {
          let lastSearch = parseJSON(getItem(RESUME_LS_RECORD));

          if (!lastSearch) {
            lastSearch = parseJSON(getItem('_newHPLastSearch'));
          } else {
            setHasLSSearch(true);
          }
          const lsRecentSearches = parseJSON(getItem(recentSearchesLocalStorageKey));

          if (lastSearch) {
            const parsed = parseResumeRecord(lastSearch);
            if (!parsed) {
              if (lsRecentSearches) setRecentSearches(lsRecentSearches);
              return;
            }
            const { lsFiltersRaw } = parsed;
            if (parsed.rawQObject) {
              setRawQObject(parsed.rawQObject);
            }

            const propertyTypeGroup = lsFiltersRaw.propertyTypeGroup || lsFiltersRaw.category;

            if (!propertyTypeGroup || lsFiltersRaw.pId) {
              setInitialLocations({
                locations: [],
                parent: {},
              });
            } else {
              const lsFilters = {
                ...lsFiltersRaw,
                propertyTypeGroup,
              };

              checkUsableFiltersValidity(lsFilters);

              const updatedFilters = {
                ...initialFilters,
                ...lsFilters,
              };              

              const locations = {
                locations: lastSearch.locations,
                parent: lastSearch.parentLocation || {},
              };

              setActiveFiltersOnSSr(getActiveFilters(updatedFilters));
              setInitialFilters(updatedFilters);
              setTempFilters(updatedFilters);
              setInitialLocations(locations);
              setRecentSearches(lsRecentSearches);

              fetchResults({
                locations: lastSearch.locations,
                filters: updatedFilters,
              });
            }
          } else if (lsRecentSearches) {
            setInitialLocations({
              locations: [],
              parent: {},
            });
            setRecentSearches(lsRecentSearches);
          }
        } catch (error) {
          console.error('❌ ERRORE durante caricamento dati LS:', error);
        }
      }
    }, 
    // Serve per il BFC.
    [navigationCycle],
  );

  useEffect(() => setAvailable(availableResults), [availableResults]);

  return (
    <div key={`search-builder-${navigationCycle}`}>
      <SearchBuilder
        initialFilters={initialFilters}
        initialLocations={initialLocations}
        fetchLocationById={fetchLocationById}
      >
        {
          (searchProps) => {
            const {
              filters,
              locations: {
                search,
                searchParent,
                ui: {
                  main,
                  additional,
                },
              },
              setCurrentNearby,
              setCurrentFilters,
              setCurrentLocation,
            } = searchProps;

            const sbfilters = {
              ...filters,
            };

            const setFilters = (newFilters) => setCurrentFilters(
              { ...filters, ...newFilters },
              (updatedFilters) => fetchResults({
                locations: search,
                filters: updatedFilters,
              }),
            );

            const getAvailableResults = async ({
              newLocations = null,
              newFilters = {},
            }) => {
              const fetchParams = {
                locations: newLocations || search,
                filters: newFilters || filters,                
              };

              const av = await fetchSBAvailableResults(fetchParams);
              return av;
            };

            const setLocations = (choice, parent) => {
              setCurrentLocation(choice, parent, {}, (updatedLocations) => fetchResults({
                locations: updatedLocations,
                filters,
              }));
            };
            const setNearby = (nearby) => setCurrentNearby(nearby, (updatedLocations, updatedParentLocation, updatedFilters) => fetchResults({ locations: updatedLocations, filters: updatedFilters }));

            const shouldForceMap = (qb) => {
              const loc = qb.locations || search;
              return loc.length === 1 && loc[0].level === 999;
            };

            const submit = (qb) => {
              submitSearch({
                locations: qb.locations || search,
                parentLocation: qb.parentLocation || searchParent,
                filters: qb.filters || filters,
                forceMap: qb.forceMap || shouldForceMap(qb),
              });
            }

            const resetAll = () => {
              const newFilters = {
                ...filters,
                ...defaultFiltersForSearchBuilder,
              };

              [
                'pId',
                'poi',
                'nearby',
                'geobounds',
                'geocircle',
                'geopolygon',
                'qExpansion',
              ].forEach((f) => {
                if (newFilters[f]) {
                  delete newFilters[f];
                }
              });

              setCurrentFilters(
                newFilters,
                (updatedFilters) => {
                  setCurrentLocation([], null, {}, (updatedLocations) => fetchResults({ locations: updatedLocations, filters: updatedFilters }));
                  setHasLSSearch(false);
                },
              );
              setActiveFiltersOnSSr(getActiveFilters(newFilters));
            };

            const startWithSelectedPtypes = initialFilters.propertyTypes && Array.isArray(initialFilters.propertyTypes)
              ? initialFilters.propertyTypes.length > 0
              : false;

            return (
              <Home
                filters={sbfilters}
                locations={search}
                parentLocation={searchParent}
                mainLocation={main}
                additionalLocations={additional}
                setFilters={setFilters}
                setLocations={setLocations}
                setNearby={setNearby}
                submit={submit}
                resetAll={resetAll}
                getAvailableResults={getAvailableResults}
                recentSearches={recentSearches}
                isFetching={isFetchingResults}
                availableResults={available}
                availablePois={availablePois}
                hasLSSearch={hasLSSearch}
                filtersDialog={filtersDialog}
                setFiltersDialog={setFiltersDialog}
                setResetDialog={setResetDialog}
                updateTempFilters={updateTempFilters}
                defaultFilters={defaultFiltersForSearchBuilder}
                tempFilters={tempFilters}
                hasEditedtempFilters={hasEditedtempFilters}
                setHasEditedTempFilters={setHasEditedTempFilters}
                startWithSelectedPtypes={startWithSelectedPtypes}
                setEraseAddressesDialog={setEraseAddressesDialog}
                activeFiltersOnSSr={activeFiltersOnSSr}
                rawQObject={rawQObject}
                {...props}
              />
            );
          }
        }
      </SearchBuilder>
      {filtersDialog}
      {resetDialog}
      {eraseAddressesDialog}
    </div>
  );
};

HomePageComponent.propTypes = {
  isLocalStorageAvailable: PropTypes.bool,
  actions: PropTypes.instanceOf(Object),
  config: PropTypes.instanceOf(Object),
  search: PropTypes.instanceOf(Object),
  intl: PropTypes.instanceOf(Object),
  location: PropTypes.instanceOf(Object),
  home: PropTypes.instanceOf(Object),
  user: PropTypes.instanceOf(Object),
  navigationCycle: PropTypes.number,
};

HomePageComponent.defaultProps = {
  isLocalStorageAvailable: false,
  actions: {},
  config: {},
  search: {},
  intl: {},
  location: {},
  home: {},
  user: {},
  navigationCycle: 0,
};

const HomePage = UiHandlerHOC(HomePageComponent, fetchHome);
export default HomePage;
